import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

interface DataTableProps {
  children: React.ReactNode;
  className?: string;
}

export function DataTable({ children, className }: DataTableProps) {
  return (
    <div
      className={cn(
        'overflow-x-auto rounded-xl border-2 border-gray-200 bg-white',
        className
      )}
    >
      <table className="w-full text-base">{children}</table>
    </div>
  );
}

interface DataTableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function DataTableHeader({ children, className }: DataTableHeaderProps) {
  return (
    <thead
      className={cn('bg-eventus-navy text-white text-left', className)}
    >
      {children}
    </thead>
  );
}

interface DataTableBodyProps {
  children: React.ReactNode;
  className?: string;
}

export function DataTableBody({ children, className }: DataTableBodyProps) {
  return (
    <tbody
      className={cn(
        'divide-y divide-gray-200 [&>tr:nth-child(odd)]:bg-white [&>tr:nth-child(even)]:bg-slate-100',
        className
      )}
    >
      {children}
    </tbody>
  );
}

interface DataTableRowProps {
  children: React.ReactNode;
  className?: string;
  highlight?: boolean;
}

export function DataTableRow({ children, className, highlight }: DataTableRowProps) {
  return (
    <tr
      className={cn(
        'transition-colors',
        highlight && 'bg-eventus-purple/10',
        className
      )}
    >
      {children}
    </tr>
  );
}

interface DataTableCellProps {
  children: React.ReactNode;
  className?: string;
  header?: boolean;
  align?: 'left' | 'center' | 'right';
}

export function DataTableCell({
  children,
  className,
  header,
  align = 'left',
}: DataTableCellProps) {
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align];

  if (header) {
    return (
      <th className={cn('px-4 py-3 font-semibold', alignClass, className)}>
        {children}
      </th>
    );
  }

  return (
    <td className={cn('px-4 py-3 text-gray-800', alignClass, className)}>{children}</td>
  );
}

// Helper component for check/x/dash indicators in tables
interface MeasureIndicatorProps {
  active: boolean | null;
  threshold?: string;
}

export function MeasureIndicator({ active, threshold }: MeasureIndicatorProps) {
  if (active === null) {
    return <span className="text-red-600 font-black text-2xl">—</span>;
  }

  if (active) {
    return (
      <div className="flex flex-col items-center">
        <Check className="w-8 h-8 text-green-600" strokeWidth={5} />
        {threshold && (
          <span className="text-[10px] font-medium text-gray-600">{threshold}</span>
        )}
      </div>
    );
  }

  return <X className="w-5 h-5 text-gray-500 mx-auto" strokeWidth={3.5} />;
}

// Convenience component for highlight values
interface HighlightValueProps {
  children: React.ReactNode;
  color?: 'purple' | 'teal' | 'pink' | 'blue' | 'green' | 'amber';
}

export function HighlightValue({ children, color = 'purple' }: HighlightValueProps) {
  const colorClasses = {
    purple: 'text-eventus-purple font-bold',
    teal: 'text-eventus-teal-dark font-bold',
    pink: 'text-pink-600 font-bold',
    blue: 'text-blue-600 font-bold',
    green: 'text-green-600 font-bold',
    amber: 'text-amber-600 font-bold',
  };

  return <span className={colorClasses[color]}>{children}</span>;
}
